import _ from 'lodash';
import decorateComponentWithProps from 'decorate-component-with-props';
import { EditorState, SelectionState } from 'draft-js';
import { findDOMNode } from 'react-dom';
import { removeBlock, isAtomicBlockFocused } from '../../modifiers/text-editor-utils';
import createStore from '../../utils/create-store';
import createDecorator from './create-decorator';
import MediaBlockEditor from './media-block-editor';
import setSelection from 'draft-js-focus-plugin/lib/modifiers/setSelectionToBlock';

const DEFAULT_MIN_CONTENT_IMAGE_WIDTH = 740;

export default function AtomicBlockPlugin(
  {
    theme,
    onUpload,
    onFileSelect,
    onVideoEmbedChange,
    isDesktop,
    getEditorRef,
    minContentImageWidth = DEFAULT_MIN_CONTENT_IMAGE_WIDTH,
  } = {}
) {
  const store = createStore();
  return {
    initialize({ getReadOnly, getEditorState, setEditorState, getEditorRef }) {
      store.updateItem('getReadOnly', getReadOnly);
      store.updateItem('getEditorState', getEditorState);
      store.updateItem('setEditorState', setEditorState);
      store.updateItem('getEditorRef', getEditorRef);
      store.updateItem('selectBlock', block => setSelection(getEditorState, setEditorState, block));
    },
    decorator: createDecorator({ store, theme }),
    handleKeyCommand(command, editorState, { setEditorState }) {
      if (isAtomicBlockFocused(editorState) && command === 'backspace') {
        setEditorState(removeBlock(editorState, editorState.getSelection().getAnchorKey()));
        return 'handled';
      }

      return 'not-handled';
    },
    blockRendererFn: (contentBlock, { getEditorState, setEditorState }) => {
      const entityKey = contentBlock.getEntityAt(0);
      if (contentBlock.type !== 'atomic' || !entityKey) {
        return null;
      }

      const entity = getEditorState().getCurrentContent().getEntity(entityKey);
      const entityData = entity.data;
      const { alignment, size } = entityData;
      const editorRef = getEditorRef && getEditorRef();
      const editorElement = editorRef && findDOMNode(editorRef);
      return {
        props: {
          deleteBlock: createDeleteEntity(contentBlock, { getEditorState, setEditorState }),
          size: !size && !alignment ? getInitialSize(minContentImageWidth, entity) : size,
          setSize: createSetSize(contentBlock, { getEditorState, setEditorState }),
          alignment,
          setAlignment: createSetAlignment(contentBlock, { getEditorState, setEditorState }),
          setHref: createSetHref(contentBlock, { getEditorState, setEditorState }),
          editorFocus: editorElement && isEditorFocused(editorElement),
        },
      };
    },
    MediaBlockEditor: decorateComponentWithProps(MediaBlockEditor, {
      pluginData: store,
      buttons: isDesktop ? getButtonsDesktop(minContentImageWidth) : getButtonsMobile,
      onUpload,
      onVideoEmbedChange,
      onFileSelect,
    }),
  };
}

function createDeleteEntity(contentBlock, { getEditorState, setEditorState }) {
  return () => setEditorState(removeBlock(getEditorState(), contentBlock.getKey()));
}

function createSetAlignment(contentBlock, { getEditorState, setEditorState }) {
  return alignment =>
    updateEntityData(contentBlock, { getEditorState, setEditorState }, entityData => {
      const result = {
        ...entityData,
        alignment,
      };
      delete result.size;
      return result;
    });
}

function createSetSize(contentBlock, { getEditorState, setEditorState }) {
  return size =>
    updateEntityData(contentBlock, { getEditorState, setEditorState }, entityData => {
      const result = {
        ...entityData,
        size,
      };
      delete result.alignment;
      return result;
    });
}

function createSetHref(contentBlock, { getEditorState, setEditorState }) {
  return href =>
    updateEntityData(contentBlock, { getEditorState, setEditorState }, entityData => ({
      ...entityData,
      href,
    }));
}

function updateEntityData(contentBlock, { getEditorState, setEditorState }, getNewData) {
  const entityKey = contentBlock.getEntityAt(0);
  if (entityKey) {
    const editorState = getEditorState();
    const contentState = editorState.getCurrentContent();
    const entityData = contentState.getEntity(entityKey).getData();
    const data = getNewData(entityData);
    contentState.replaceEntityData(entityKey, data);
    const newSelection = new SelectionState({
      anchorKey: contentBlock.key,
      anchorOffset: 0,
      focusKey: contentBlock.key,
      focusOffset: 0,
    });
    setEditorState(EditorState.forceSelection(editorState, newSelection));
  }
}

function getInitialSize(minContentImageWidth, { type, data }) {
  if (isImage(type)) {
    return getImageWidth(data) >= minContentImageWidth ? 'content' : 'original';
  }

  return 'content';
}

function getImageWidth(entityData) {
  return _.get(entityData, 'src.width', 0);
}

function getButtonsMobile({ type }) {
  return [isImage(type) ? 'reupload' : null, 'delete'];
}

function getButtonsDesktop(minContentImageWidth) {
  return ({ type, data }) => [
    isImage(type) && isSmallImage(minContentImageWidth, data) ? 'sizeOriginal' : null,
    'sizeText',
    'sizeContent',
    'sizeFullWidth',
    '-',
    'alignLeft',
    'alignRight',
    '-',
    isImage(type) ? 'reupload' : null,
    'delete',
  ];
}

function isImage(type) {
  return type === 'IMAGE';
}

function isSmallImage(minContentImageWidth, entityData) {
  return getImageWidth(entityData) < minContentImageWidth;
}

function isEditorFocused(editorElement) {
  if (typeof window === 'undefined') {
    return false;
  }

  const activeElement = document.activeElement;
  return activeElement && isFocused(activeElement, editorElement);
}

function isFocused(activeElement, editor) {
  if (activeElement === editor) {
    return true;
  }

  if (!activeElement.parentNode) {
    return false;
  }

  return isFocused(activeElement.parentNode, editor);
}



// WEBPACK FOOTER //
// ./app/components/text-editor/plugins/media-block/index.js